
import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Shield, Eye, EyeOff, AlertCircle, Lock } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { InputValidator } from "../utils/validation";

const Login = () => {
  const { login, isLoading, error } = useAuth();
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [loginData, setLoginData] = useState({
    username: "",
    password: "",
    twoFactorCode: ""
  });
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  const validateForm = () => {
    const errors: string[] = [];
    
    const usernameValidation = InputValidator.validateUsername(loginData.username);
    if (!usernameValidation.isValid) {
      errors.push(...usernameValidation.errors);
    }
    
    if (!loginData.password || loginData.password.length < 4) {
      errors.push('Password is required');
    }
    
    if (requiresTwoFactor && (!loginData.twoFactorCode || loginData.twoFactorCode.length !== 6)) {
      errors.push('Please enter a valid 6-digit 2FA code');
    }

    // Check for malicious input
    if (InputValidator.detectXSS(loginData.username) || 
        InputValidator.detectXSS(loginData.password) ||
        InputValidator.detectSQLInjection(loginData.username)) {
      errors.push('Invalid characters detected');
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (attempts >= maxAttempts) {
      toast({
        title: "Account Temporarily Locked",
        description: "Too many failed attempts. Please try again later.",
        variant: "destructive",
      });
      return;
    }

    if (!validateForm()) {
      return;
    }
    
    try {
      const result = await login(
        InputValidator.sanitizeInput(loginData.username), 
        loginData.password,
        loginData.twoFactorCode
      );
      
      if (result.success) {
        toast({
          title: "Login Successful",
          description: "Welcome to the BPP Platform",
        });
        setAttempts(0);
      } else if (result.requiresTwoFactor) {
        setRequiresTwoFactor(true);
        toast({
          title: "Two-Factor Authentication Required",
          description: "Please enter your 2FA code to continue",
        });
      } else {
        setAttempts(prev => prev + 1);
        toast({
          title: "Login Failed",
          description: result.error || "Invalid credentials",
          variant: "destructive",
        });
      }
    } catch (error) {
      setAttempts(prev => prev + 1);
      toast({
        title: "Login Error",
        description: "Authentication service temporarily unavailable",
        variant: "destructive",
      });
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setLoginData(prev => ({ ...prev, [field]: value }));
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const userRoles = [
    { id: "supplier", name: "Supplier", description: "Upload pricing, submit bids", color: "bg-blue-500" },
    { id: "bidder", name: "Premium Bidder", description: "Access pricing data, competitor insights", color: "bg-purple-500" },
    { id: "procurement", name: "Procurement Officer", description: "Approve tenders, track contracts", color: "bg-green-500" },
    { id: "admin", name: "Admin", description: "System control, audit logs", color: "bg-red-500" },
    { id: "director", name: "Director", description: "Ministry-specific data access", color: "bg-yellow-500" },
    { id: "auditor", name: "Auditor", description: "Read-only financial logs", color: "bg-gray-500" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-yellow-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="https://www.bpp.gov.ng/wp-content/uploads/2019/02/BPP-LOGO-2-1.png" 
              alt="BPP Logo" 
              className="h-12 w-auto"
            />
          </div>
          <CardTitle className="text-2xl font-bold">
            {requiresTwoFactor ? "Two-Factor Authentication" : "Secure Login"}
          </CardTitle>
          <CardDescription>
            {requiresTwoFactor 
              ? "Enter your 6-digit authentication code"
              : "Access the Nigeria National Procurement Platform"
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {attempts >= maxAttempts && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2 text-red-700">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Account Locked</span>
              </div>
              <p className="text-xs text-red-600 mt-1">
                Too many failed attempts. Please contact support.
              </p>
            </div>
          )}

          {!requiresTwoFactor && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-2 text-blue-700">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Demo Access</span>
              </div>
              <p className="text-xs text-blue-600 mt-1">
                Admin: admin / admin (requires 2FA: 123456)
              </p>
            </div>
          )}

          {validationErrors.length > 0 && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2 text-red-700 mb-1">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Validation Errors</span>
              </div>
              {validationErrors.map((error, index) => (
                <p key={index} className="text-xs text-red-600">• {error}</p>
              ))}
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2 text-red-700">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{error}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            {!requiresTwoFactor ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="Enter your username"
                    value={loginData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    required
                    disabled={isLoading || attempts >= maxAttempts}
                    maxLength={50}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={loginData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      required
                      disabled={isLoading || attempts >= maxAttempts}
                      maxLength={128}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading || attempts >= maxAttempts}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="twoFactorCode">Two-Factor Authentication Code</Label>
                <Input
                  id="twoFactorCode"
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={loginData.twoFactorCode}
                  onChange={(e) => handleInputChange('twoFactorCode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  disabled={isLoading}
                  maxLength={6}
                  pattern="[0-9]{6}"
                />
                <p className="text-xs text-gray-600">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full"
              style={{ backgroundColor: '#0F7D37' }}
              disabled={isLoading || attempts >= maxAttempts}
            >
              {requiresTwoFactor ? (
                <>
                  <Lock className="h-4 w-4 mr-2" />
                  {isLoading ? "Verifying..." : "Verify & Login"}
                </>
              ) : (
                <>
                  <Shield className="h-4 w-4 mr-2" />
                  {isLoading ? "Authenticating..." : "Secure Login"}
                </>
              )}
            </Button>

            {requiresTwoFactor && (
              <Button 
                type="button" 
                variant="outline"
                className="w-full"
                onClick={() => {
                  setRequiresTwoFactor(false);
                  setLoginData(prev => ({ ...prev, twoFactorCode: "" }));
                }}
                disabled={isLoading}
              >
                Back to Login
              </Button>
            )}
          </form>

          {!requiresTwoFactor && (
            <>
              <div className="mt-6">
                <div className="text-sm text-center text-gray-600 mb-3">Available User Roles:</div>
                <div className="space-y-2">
                  {userRoles.map((role) => (
                    <div key={role.id} className="flex items-center space-x-2 text-xs">
                      <div className={`w-2 h-2 rounded-full ${role.color}`}></div>
                      <span className="font-medium">{role.name}:</span>
                      <span className="text-gray-600">{role.description}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 text-center text-sm">
                <a href="#" className="text-green-600 hover:underline">
                  Forgot password?
                </a>
                <span className="mx-2">|</span>
                <a href="#" className="text-green-600 hover:underline">
                  Register as Supplier
                </a>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
